import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";

const COLORS = {
  completed: "#10b981", // emerald-500
  failed: "#f43f5e", // rose-500
  cancelled: "#94a3b8", // slate-400
};

const STATUS_LABEL = {
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
  running: "Running",
  pending: "Pending",
};

function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  const diff = Math.max(0, Date.now() - d);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Read-only mini SVG showing 7-day stacked bars per workflow.
 * Props:
 *   - workflowIds: string[]   the workflow_ids visible in the current table page
 *   - getRow: (workflow_id) => activitySummary    pre-fetched lookup
 * For convenience this component owns the batch fetch itself.
 */
export default function WorkflowActivityCells({ workflowIds }) {
  const idsKey = workflowIds.slice().sort().join(",");
  const summary = useQuery({
    queryKey: ["activity-summary", idsKey, 7],
    queryFn: async () =>
      (
        await api.get("/workflows/activity-summary", {
          params: { ids: idsKey, days: 7 },
        })
      ).data,
    enabled: workflowIds.length > 0,
    staleTime: 30_000,
  });

  const lookup = {};
  (summary.data || []).forEach((row) => {
    lookup[row.workflow_id] = row;
  });

  return { lookup, isLoading: summary.isLoading };
}

/**
 * Single-row presentation: sparkline + last-run chip.
 * Pass either `data` (the row from /activity-summary) or just `workflowId` to
 * fetch single-shot.
 */
export function ActivityCell({ data, workflowId, isLoading }) {
  if (isLoading) {
    return (
      <div
        className="h-7 w-28 animate-pulse rounded bg-slate-100"
        data-testid={`activity-cell-loading-${workflowId}`}
      />
    );
  }
  const empty =
    !data ||
    !data.daily_counts ||
    data.daily_counts.every(
      (d) => d.completed + d.failed + d.cancelled === 0,
    );
  return (
    <div
      className="flex items-center gap-3"
      data-testid={`activity-cell-${workflowId}`}
    >
      <Sparkline data={data?.daily_counts} />
      <LastRunChip
        empty={empty}
        lastRunAt={data?.last_run_at}
        lastRunStatus={data?.last_run_status}
        lastRunNumber={data?.last_run_number}
      />
    </div>
  );
}

function Sparkline({ data }) {
  const days = data || Array.from({ length: 7 }).map(() => ({
    completed: 0,
    failed: 0,
    cancelled: 0,
    date: "",
  }));
  const max = Math.max(
    1,
    ...days.map((d) => d.completed + d.failed + d.cancelled),
  );
  const w = 120;
  const h = 28;
  const gap = 2;
  const barW = (w - gap * (days.length - 1)) / days.length;
  return (
    <svg
      width={w}
      height={h}
      role="img"
      aria-label="7-day run activity"
      data-testid="activity-sparkline"
    >
      {days.map((d, i) => {
        const total = d.completed + d.failed + d.cancelled;
        const stackH = (total / max) * (h - 2);
        const x = i * (barW + gap);
        let y = h;
        return (
          <g key={`${d.date}-${i}`}>
            <title>
              {d.date}: ✓{d.completed} ✗{d.failed} ↺{d.cancelled}
            </title>
            {["completed", "failed", "cancelled"].map((k) => {
              const segH = d[k] ? (d[k] / max) * (h - 2) : 0;
              y -= segH;
              if (!segH) return null;
              return (
                <rect
                  key={k}
                  x={x}
                  y={y}
                  width={barW}
                  height={segH}
                  fill={COLORS[k]}
                  rx={1}
                />
              );
            })}
            {total === 0 && (
              <rect
                x={x}
                y={h - 1}
                width={barW}
                height={1}
                fill="#e2e8f0"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function LastRunChip({ empty, lastRunAt, lastRunStatus, lastRunNumber }) {
  if (empty || !lastRunAt) {
    return (
      <span
        className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500"
        data-testid="last-run-chip-empty"
      >
        Never run
      </span>
    );
  }
  const map = {
    completed: { cls: "bg-emerald-50 text-emerald-700", glyph: "✓" },
    failed: { cls: "bg-rose-50 text-rose-700", glyph: "✗" },
    cancelled: { cls: "bg-slate-100 text-slate-600", glyph: "↺" },
    running: { cls: "bg-indigo-50 text-indigo-700", glyph: "•" },
    pending: { cls: "bg-slate-100 text-slate-600", glyph: "…" },
  };
  const m = map[lastRunStatus] || map.pending;
  const runLabel =
    lastRunNumber != null
      ? `Run #${String(lastRunNumber).padStart(4, "0")} · `
      : "";
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${m.cls}`}
      data-testid={`last-run-chip-${lastRunStatus}`}
      title={`${runLabel}${STATUS_LABEL[lastRunStatus]} · ${new Date(lastRunAt).toLocaleString()}`}
    >
      {m.glyph} {timeAgo(lastRunAt)}
    </span>
  );
}
